import React from 'react';
import { X, Building, CheckCircle, XCircle, Calendar, CreditCard } from 'lucide-react';
import { Client } from '../../types/client';
import { formatDate } from '../../utils/dateUtils';

interface ClientDetailsModalProps {
  client: Client;
  accountCount?: number;
  onClose: () => void;
}

export const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  client,
  accountCount,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Client Details</h3>
              <p className="text-sm text-gray-600 mt-1">{client.client_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex items-center space-x-2">
              {client.is_active ? (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Active Client
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  <XCircle className="h-4 w-4 mr-2" />
                  Inactive Client
                </span>
              )}
            </div>
          </div>

          {/* Client Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
              <p className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded">
                #{client.id}
              </p>
            </div>

            {/* Account Count */}
            {accountCount !== undefined && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Accounts</label>
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">
                    {accountCount} {accountCount === 1 ? 'account' : 'accounts'}
                  </p>
                </div>
              </div>
            )}

            {/* Created Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <p className="text-sm text-gray-900">
                  {formatDate(client.created_at)}
                </p>
              </div>
            </div>

            {/* Last Updated */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <p className="text-sm text-gray-900">
                  {formatDate(client.updated_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Client Information</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <p>
                <span className="font-medium">Name:</span> {client.client_name}
              </p>
              <p>
                <span className="font-medium">Status:</span>{' '}
                {client.is_active ? 'Active and accepting new accounts' : 'Inactive - no new accounts'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
